<script setup lang="ts">
import { computed, nextTick } from "vue";
import { useRouter } from "vue-router";
import { DynamicScrollerItem } from "vue-virtual-scroller";

import Button from "~/components/dumb/Button.vue";
import DropdownMenu from "~/components/dumb/DropdownMenu.vue";
import LoadingSpinner from "~/components/dumb/LoadingSpinner.vue";
import UpdateTime from "~/components/dumb/UpdateTime.vue";
import { copyAndNotify } from "~/components/notifications";
import CommentEditor from "~/components/text/CommentEditor.vue";
import DartLink from "~/components/text/nodes/DartLink.vue";
import {
  ChevronLeftDoubleIcon,
  DotsHorizontalIcon,
  DuidFieldIcon,
  FullscreenIcon,
  ReadIcon,
  UnreadIcon,
} from "~/icons";
import {
  ButtonSize,
  ButtonStyle,
  DropdownMenuItemKind,
  EntityName,
  InboxFilter,
  TaskDetailMode,
  TextSentinel,
} from "~/shared/enums";
import type { MessageFrag, Notification } from "~/shared/types";
import { useAppStore, useDataStore, usePageStore, useTenantStore, useUserStore } from "~/stores";
import { asyncState } from "~/utils/async";
import { isString } from "~/utils/common";

const ENTITY_KINDS_TO_OPEN_IN_DETAIL = new Set([EntityName.TASK, EntityName.COMMENT]);

const props = defineProps<{
  notification: Notification;
  activeInScroll: boolean;
  selected: boolean;
  justRead: boolean;
}>();

const emit = defineEmits<{
  select: [];
  markUnread: [];
  clear: [];
}>();

const router = useRouter();
const appStore = useAppStore();
const dataStore = useDataStore();
const pageStore = usePageStore();
const tenantStore = useTenantStore();
const userStore = useUserStore();

const shouldOpenInDetail = computed(() => ENTITY_KINDS_TO_OPEN_IN_DETAIL.has(props.notification.event.mainEntityName));

// Fetches the entity data based on the main entity name
const { state: entity, isLoading: isLoadingEntity } = asyncState(
  async () => dataStore.getNotificationEntity(props.notification),
  {
    default: undefined,
    watch: () => props.notification,
  }
);

const entityTitle = computed(() => entity.value?.title ?? `(Unknown ${props.notification.event.mainEntityName})`);

const clickNotificationBody = () => {
  emit("select");

  if (!shouldOpenInDetail.value) {
    appStore.selectedTaskDuids = new Set();
    appStore.setTaskDetailOpen(false);
    return;
  }

  if (props.notification.event.taskDuid) {
    appStore.selectedTaskDuids = new Set([props.notification.event.taskDuid]);
    appStore.setTaskDetailOpen(true);
  }

  if (props.notification.event.mainEntityName === EntityName.COMMENT) {
    nextTick(() => {
      router.push({ query: { c: props.notification.event.commentDuid } });
      appStore.taskDetail?.jumpToComments(false);
    });
  }
};

const clickNotificationOpenButton = async () => {
  if (entity.value === undefined) {
    return;
  }

  const { mainEntityName } = props.notification.event;
  const link = await dataStore.getEntityLink(entity.value, mainEntityName);
  if (link) {
    router.push(link);
  }
};

const toggleNotificationRead = () => {
  const oldRead = props.notification.read;
  dataStore.updateNotification({ duid: props.notification.duid, read: !oldRead });
  if (oldRead) {
    emit("markUnread");
  }
};

const clearOrUpdateNotificationRead = () => {
  if (props.justRead) {
    emit("clear");
    return;
  }

  toggleNotificationRead();
};

const isTextSentinel = (frag: MessageFrag): frag is TextSentinel =>
  Object.values(TextSentinel).includes(frag as TextSentinel);

const processFrags = (frags: MessageFrag[] | null) => {
  const res: [Exclude<MessageFrag, TextSentinel>, boolean][] = [];
  if (frags === null) {
    return res;
  }
  frags.forEach((frag, index) => {
    if (isTextSentinel(frag)) {
      return;
    }
    res.push([frag, index < frags.length - 1 && frags[index + 1] !== TextSentinel.NO_SPACE]);
  });
  return res;
};

// TODO this is not a great solution because it bypasses the normal message with message frags and hardcodes that any comment message frag is this one. This will break when we have any notification with multiple comments in it, for example.
const commentWithTask = computed(() => {
  if (props.notification.event.mainEntityName !== EntityName.COMMENT || !props.notification.event.commentDuid) {
    return undefined;
  }
  const comment = dataStore.getCommentByDuidIfLoaded(props.notification.event.commentDuid);
  if (!comment) {
    return undefined;
  }
  const task = dataStore.getTaskByDuidIfLoaded(comment.taskDuid);
  if (!task) {
    return undefined;
  }
  return {
    comment,
    task,
  };
});

const sections = computed(() => [
  {
    title: "Manage",
    items: [
      {
        title: "Clear",
        kind: DropdownMenuItemKind.BUTTON,
        icon: ReadIcon,
        hidden: appStore.inbox?.filter !== InboxFilter.UNREAD || !props.justRead,
        onClick: () => clearOrUpdateNotificationRead(),
      },
      {
        title: `Mark as ${props.notification.read ? "un" : ""}read`,
        kind: DropdownMenuItemKind.BUTTON,
        icon: props.notification.read ? UnreadIcon : ReadIcon,
        onClick: () => toggleNotificationRead(),
      },
      {
        title: "Copy ID",
        kind: DropdownMenuItemKind.BUTTON,
        icon: DuidFieldIcon,
        onClick: () => copyAndNotify("Notification ID", props.notification.duid),
      },
    ],
  },
]);

const onContextMenu = (event: MouseEvent) => {
  if (tenantStore.isDart && !pageStore.adminHidden && event.altKey) {
    return;
  }
  appStore.openContextMenu(event as PointerEvent, sections.value);
};
</script>

<template>
  <DynamicScrollerItem
    :item="notification"
    :active="activeInScroll"
    :size-dependencies="[]"
    class="group/notification relative flex cursor-pointer gap-1 border-b py-3 pl-4 pr-2.5 border-md"
    :class="[
      selected ? 'bg-primary-base/15 hover:bg-primary-base/25' : 'hover:bg-lt',
      isLoadingEntity && 'pointer-events-none',
      isLoadingEntity ? 'justify-end' : 'justify-between',
    ]"
    @contextmenu="onContextMenu"
    @click.stop="clickNotificationBody"
    @keydown.enter.stop="clickNotificationBody">
    <div v-if="isLoadingEntity" class="absolute inset-0 flex items-center justify-center">
      <LoadingSpinner />
    </div>

    <div v-else class="flex grow select-none flex-col justify-between gap-1">
      <div class="font-semibold text-md">
        {{ entityTitle }}
      </div>
      <div class="flex grow gap-2">
        <div class="mr-2.5 flex w-full min-w-0 flex-wrap items-center text-sm">
          <template
            v-for="([item, hasSpaceAfter], index) in processFrags(notification.event.messageFrags)"
            :key="index">
            <div v-if="item === '\n'" class="w-full" />
            <span v-else-if="isString(item)" :class="hasSpaceAfter && 'mr-1'" class="mb-px flex items-center text-lt">
              {{ item.replaceAll(/ /g, "&nbsp;") }}
            </span>
            <div
              v-else-if="item.entityName === EntityName.COMMENT && commentWithTask"
              class="border-l-4 pl-3 border-hvy">
              <CommentEditor :task="commentWithTask.task" :comment="commentWithTask.comment" :editing="false" />
            </div>
            <DartLink
              v-else
              is-notification-mode
              :name="item.entityName"
              :duid="item.duid"
              :class="hasSpaceAfter ? 'mr-0.5' : '-mr-1'"
              class="-ml-0.5"
              @open="clickNotificationBody"
              @click.stop
              @keydown.enter.stop />
          </template>
        </div>
      </div>
    </div>

    <div class="-mt-0.5 mb-0.5 mr-1.5 flex shrink-0 flex-col items-end justify-between gap-2">
      <div class="flex items-center gap-1.5">
        <UpdateTime :at="notification.createdAt" cursor-pointer />
        <div
          v-if="!notification.read"
          class="-mr-1.5 flex items-center justify-center rounded-full focus-ring-none icon-md">
          <div class="size-2 rounded-full bg-primary-base" />
        </div>
      </div>

      <div class="flex gap-1.5" :class="pageStore.isNarrowScreen ? 'flex-col items-end' : 'items-center'">
        <DropdownMenu
          :sections="sections"
          :width-pixels="192"
          :class="!selected && 'opacity-0 group-hover/notification:opacity-100'"
          @click.stop>
          <div class="flex size-7 items-center justify-center rounded text-lt hover:bg-md">
            <span class="sr-only">Manage notification</span>
            <DotsHorizontalIcon class="icon-md" />
          </div>
        </DropdownMenu>

        <Button
          v-if="!(selected && appStore.taskOpenInDetail)"
          :btn-style="ButtonStyle.SECONDARY"
          :icon="
            pageStore.isMobile || !shouldOpenInDetail || userStore.taskDetailMode !== TaskDetailMode.RIGHTBAR
              ? FullscreenIcon
              : ChevronLeftDoubleIcon
          "
          text="Open"
          :size="ButtonSize.SMALL"
          class="h-7 !gap-1.5 pl-1 pr-1.5 hover:!bg-gray-900/10 dark:hover:!bg-white/10"
          :class="!selected && 'opacity-0 group-hover/notification:opacity-100'"
          @click="clickNotificationOpenButton" />

        <Button
          :btn-style="ButtonStyle.PRIMARY"
          :icon="justRead || !notification.read ? ReadIcon : UnreadIcon"
          :text="justRead ? 'Clear' : `Mark ${notification.read ? 'un' : ''}read`"
          :size="ButtonSize.SMALL"
          class="h-7 !gap-1.5 pl-1 pr-1.5"
          :class="!selected && 'opacity-0 group-hover/notification:opacity-100'"
          @click.stop="clearOrUpdateNotificationRead" />
      </div>
    </div>
  </DynamicScrollerItem>
</template>
